"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { strings } from "@perceptron/constants";
import {
  AnnotationSource,
  AnnotationType,
  BoxAnnotation,
  isBoxAnnotation,
  isCollectionAnnotation,
  isPointAnnotation,
  PointAnnotation,
} from "@perceptron/core/types";
import { Button } from "@perceptron/ui/components/button";
import { Icon } from "@perceptron/ui/components/icon";
import { Skeleton } from "@perceptron/ui/components/skeleton";
import { cn } from "@perceptron/utils/cn";
import { animated, useTransition } from "@react-spring/web";

import PointAnnotationText from "@demo/components/Annotation/PointAnnotationText";
import { StreamSourceProvider } from "@demo/contexts/StreamSourceContext";
import { useFeaturedTemplates } from "@demo/features/examples/useFeaturedTemplates";
import { useAnnotationInteractions } from "@demo/hooks/annotations/useAnnotationInteractions";
import { removeDetectCategory } from "@demo/libs/detect";
import { useDetectStore } from "@demo/store/detect";
import { useModelStore } from "@demo/store/model";

/** A detection's geometry as shown in the panel: a box or a point. */
type DetectionAnnotation = BoxAnnotation | PointAnnotation;

interface CategoryGroup {
  label: string;
  items: DetectionAnnotation[];
}

const CategoryCard = ({
  group,
  hasResult,
  isRunning,
  onRemove,
}: {
  group: CategoryGroup;
  hasResult: boolean;
  isRunning: boolean;
  onRemove: () => void;
}) => {
  const { handleMouseEnter, handleMouseLeave } = useAnnotationInteractions();
  const itemIds = useMemo(() => group.items.map((i) => i.id), [group.items]);

  return (
    <div
      className="group flex flex-col"
      onMouseEnter={() => {
        if (itemIds.length > 0) handleMouseEnter(itemIds, AnnotationSource.CONTENT);
      }}
      onMouseLeave={() => {
        const first = itemIds[0];
        if (first) handleMouseLeave(first);
      }}
      onMouseMove={(e) => {
        if (itemIds.length === 0) return;
        if ((e.target as Element).closest("[data-annotation-type]")) return;
        handleMouseEnter(itemIds, AnnotationSource.CONTENT);
      }}
    >
      <div className="flex items-center justify-between">
        <div className="group/tab flex items-center gap-2 rounded-t-md bg-background-primary px-4 pt-1.5">
          <span className="text-foreground-primary label-2">{group.label}</span>
          {hasResult && <span className="text-foreground-secondary label-2">{group.items.length}</span>}
        </div>
        <Button
          aria-label={strings.detect.removeCategoryAriaLabel}
          className="h-auto w-auto p-1 text-foreground-secondary opacity-0 transition-all hover:bg-transparent hover:text-error group-hover:opacity-100"
          size="icon"
          variant="ghost"
          onClick={onRemove}
        >
          <Icon className="size-4" name="trash-2" />
        </Button>
      </div>
      <div
        className={cn(
          "rounded-b-md rounded-tr-md bg-background-primary p-4",
          group.items.length === 0 && "flex items-center justify-center",
        )}
      >
        {group.items.length > 0 ? (
          <div className="flex flex-wrap gap-0.5">
            {group.items.map((item) => (
              <PointAnnotationText
                data-annotation-type={isBoxAnnotation(item) ? AnnotationType.BOX : AnnotationType.POINT}
                data-streaming="false"
                key={item.id}
                number={Number(item.id.split("-").pop())}
              />
            ))}
          </div>
        ) : isRunning ? (
          <Skeleton className="h-4 w-full rounded-sm bg-white/10" />
        ) : hasResult ? (
          <div>
            <p className="italic text-foreground-disabled legal">{strings.detect.noDetections}</p>
          </div>
        ) : (
          <div>
            <p className="italic text-foreground-disabled legal">{strings.detect.detectionsWillAppear}</p>
          </div>
        )}
      </div>
    </div>
  );
};

const CategoryList = () => {
  const categories = useDetectStore((state) => state.categories);
  const isRunning = useDetectStore((state) => state.status === "running");
  const isDone = useDetectStore((state) => state.status === "done");
  const requestedCategories = useDetectStore((state) => state.requestedCategories);
  const annotations = useModelStore((state) => state.annotations);

  const featured = useFeaturedTemplates("detect");
  const hasImage = useDetectStore((state) => !!state.image);
  const hasPending = useDetectStore((state) => state.pendingCategory.trim().length > 0);
  const examplesVisible = featured.length > 0 && !hasImage && !hasPending && categories.length === 0;

  const detected = useMemo(() => {
    const map = new Map<string, DetectionAnnotation[]>();
    for (const annotation of annotations) {
      if (!isCollectionAnnotation(annotation)) continue;
      map.set(
        annotation.label,
        annotation.children.filter((c): c is DetectionAnnotation => isBoxAnnotation(c) || isPointAnnotation(c)),
      );
    }
    return map;
  }, [annotations]);

  const requestedSet = useMemo(() => new Set(requestedCategories.map((c) => c.toLowerCase())), [requestedCategories]);

  const groups = useMemo<CategoryGroup[]>(() => {
    const userSet = new Set(categories.map((c) => c.toLowerCase()));
    const rows: CategoryGroup[] = categories.map((label) => ({
      label,
      items: detected.get(label) ?? [],
    }));
    for (const [label, items] of detected.entries()) {
      if (!userSet.has(label.toLowerCase())) {
        rows.push({ label, items });
      }
    }
    return rows;
  }, [categories, detected]);

  const scrollRef = useRef<HTMLDivElement>(null);
  const prevCount = useRef(categories.length);
  const pinToBottom = useRef(false);

  useEffect(() => {
    if (categories.length > prevCount.current) pinToBottom.current = true;
    prevCount.current = categories.length;
  }, [categories.length]);

  const [scrollFade, setScrollFade] = useState({ top: false, bottom: false });
  const updateScrollFade = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const top = el.scrollTop > 1;
    const bottom = el.scrollTop + el.clientHeight < el.scrollHeight - 1;
    setScrollFade((prev) => (prev.top === top && prev.bottom === bottom ? prev : { top, bottom }));
  }, []);
  // Re-measure after each render (content changes); scroll + animation also drive it below.
  useEffect(() => {
    updateScrollFade();
  });

  const maskImage = useMemo(() => {
    const top = scrollFade.top ? "transparent, black 24px" : "black";
    const bottom = scrollFade.bottom ? "black calc(100% - 24px), transparent" : "black";
    return `linear-gradient(to bottom, ${top}, ${bottom})`;
  }, [scrollFade]);

  const transitions = useTransition(groups, {
    keys: (group) => group.label,
    initial: { opacity: 1, transform: "translateY(0px)", filter: "blur(0px)", gridTemplateRows: "1fr" },
    from: { opacity: 0, transform: "translateY(96px)", filter: "blur(16px)", gridTemplateRows: "1fr" },
    enter: { opacity: 1, transform: "translateY(0px)", filter: "blur(0px)", gridTemplateRows: "1fr" },
    leave: { opacity: 0, transform: "translateY(0px)", filter: "blur(16px)", gridTemplateRows: "0fr" },
    config: { tension: 280, friction: 30 },
    onChange: () => {
      if (pinToBottom.current && scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
      updateScrollFade();
    },
    onRest: () => {
      pinToBottom.current = false;
    },
  });

  return (
    <StreamSourceProvider source={AnnotationSource.CONTENT}>
      <div
        className="flex flex-1 flex-col overflow-y-auto"
        ref={scrollRef}
        style={{ maskImage, WebkitMaskImage: maskImage }}
        onScroll={updateScrollFade}
      >
        <div
          className={cn(
            "absolute inset-0 flex h-full w-full flex-1 flex-col items-center justify-center pb-16 transition-opacity",
            {
              "z-0 opacity-0": !!groups.length,
              "opacity-full": !groups.length,
            },
            examplesVisible && "max-sm:hidden",
          )}
        >
          <div className="flex flex-col items-center gap-2 text-foreground-disabled label-2">
            <Icon name="lightbulb" />
            <div className="max-w-48 text-center">{strings.detect.detectEverything}</div>
          </div>
        </div>
        {transitions((style, group) => {
          const hasResult = group.items.length > 0 || (isDone && requestedSet.has(group.label.toLowerCase()));
          return (
            <animated.div className="grid" style={style}>
              <div className="min-h-0 overflow-hidden">
                <div className="pb-4">
                  <CategoryCard
                    group={group}
                    hasResult={hasResult}
                    isRunning={isRunning}
                    onRemove={() => removeDetectCategory(group.label)}
                  />
                </div>
              </div>
            </animated.div>
          );
        })}
      </div>
    </StreamSourceProvider>
  );
};

export default CategoryList;
